import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useCurrency } from '@/hooks/useCurrency';

import { UsageStatusBadge } from '../components/UsageStatusBadge';
import {
  useUsageAllLinesQuery,
  useUsageLinesQuery,
  useUsageMonitoringFilters,
  useUsageSupplierDaysQuery,
} from '../hooks/useUsageMonitoring';
import { usageMonitoringService } from '../services/usageMonitoringService';
import type { UsageLineRow, UsageSupplierDayRow } from '../types';
import {
  formatCount,
  formatSignedNumber,
  formatSignedRatioPct,
  signedDeltaClassName,
  summarizeLinesBias,
} from '../utils/format';

function creatorLabel(row: UsageSupplierDayRow): string {
  const creators = row.creators ?? [];
  if (!creators.length) return '—';
  return creators
    .map((c) => {
      const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
      return full || c.username || String(c.id);
    })
    .join(', ');
}

export function UsageMonitoringDetailPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'es';
  const { formatRounded } = useCurrency();
  const { isActionEnabled } = useFeatureVisibility();
  const allowExport = isActionEnabled('usage_monitoring.export_excel', false);
  const {
    queryParams,
    filters,
    setDetailStatus,
    setDetailPresence,
  } = useUsageMonitoringFilters();

  const statusFilter = filters.detailStatus;
  const presenceFilter = filters.detailPresence;

  const [page, setPage] = useState(1);
  const [supplierDaysPageSize, setSupplierDaysPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [selected, setSelected] = useState<UsageSupplierDayRow | null>(null);
  const [linesPage, setLinesPage] = useState(1);
  const [linesPageSize, setLinesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
    setSelected(null);
    setLinesPage(1);
  }, [
    queryParams.date_from,
    queryParams.date_to,
    queryParams.user,
    queryParams.supplier,
    queryParams.warehouse,
    statusFilter,
  ]);

  const showGlobalLines = Boolean(presenceFilter) && !selected;

  const supplierDaysParams = useMemo(
    () => ({
      ...queryParams,
      status: statusFilter || undefined,
      page,
      page_size: supplierDaysPageSize,
    }),
    [queryParams, statusFilter, page, supplierDaysPageSize],
  );

  const linesParams = useMemo(() => {
    if (selected) {
      return {
        ...queryParams,
        order_date: selected.order_date,
        supplier: selected.supplier_id,
        warehouse: selected.warehouse_id ?? undefined,
        presence_status: presenceFilter || undefined,
        page: linesPage,
        page_size: linesPageSize,
      };
    }
    if (showGlobalLines) {
      return {
        ...queryParams,
        presence_status: presenceFilter || undefined,
        page: linesPage,
        page_size: linesPageSize,
      };
    }
    return null;
  }, [queryParams, selected, linesPage, linesPageSize, presenceFilter, showGlobalLines]);

  const summaryParams = useMemo(() => {
    if (!linesParams) return null;
    const { page: _page, page_size: _pageSize, ...rest } = linesParams;
    return rest;
  }, [linesParams]);

  const supplierDaysQuery = useUsageSupplierDaysQuery(supplierDaysParams);
  const linesQuery = useUsageLinesQuery(linesParams ?? {}, Boolean(linesParams));
  const allLinesQuery = useUsageAllLinesQuery(summaryParams, Boolean(summaryParams));
  const biasSummary = useMemo(
    () => summarizeLinesBias(allLinesQuery.data ?? []),
    [allLinesQuery.data],
  );

  const supplierDayColumns = useMemo<Column[]>(
    () => [
      {
        header: t('adoption.detail.columns.date'),
        accessor: 'order_date',
        sortable: true,
        width: '110px',
      },
      {
        header: t('adoption.detail.columns.supplier'),
        accessor: 'supplier_name',
        sortable: true,
        sortValue: (_v, row: UsageSupplierDayRow) =>
          `${row.supplier_code ?? ''} ${row.supplier_name ?? ''}`,
        cell: (_v, row: UsageSupplierDayRow) =>
          row.supplier_code
            ? `${row.supplier_code} — ${row.supplier_name ?? ''}`
            : row.supplier_name ?? '—',
      },
      {
        header: t('adoption.detail.columns.status'),
        accessor: 'status',
        sortable: true,
        cell: (value: string) => <UsageStatusBadge status={value} />,
        width: '180px',
      },
      {
        header: t('adoption.detail.columns.lines'),
        accessor: 'lines_total',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '90px',
        cell: (_v, row: UsageSupplierDayRow) =>
          t('adoption.detail.linesBreakdown', {
            total: formatCount(row.lines_total, locale),
            both: formatCount(row.lines_in_both, locale),
            erp: formatCount(row.lines_solo_erp, locale),
            stocker: formatCount(row.lines_solo_stockerai, locale),
          }),
      },
      {
        header: t('adoption.detail.columns.deltaTotal'),
        accessor: 'delta_total',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '100px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (
          <span className={signedDeltaClassName(value)}>
            {formatSignedNumber(value, locale)}
          </span>
        ),
      },
      {
        header: t('adoption.detail.columns.deltaTotalAmount'),
        accessor: 'delta_total_amount',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '110px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) =>
          value == null ? (
            '—'
          ) : (
            <span className={signedDeltaClassName(value)}>{formatRounded(value)}</span>
          ),
      },
      {
        header: t('adoption.detail.columns.totalBias'),
        accessor: 'total_bias_ratio',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '100px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (
          <span className={signedDeltaClassName(value)}>
            {formatSignedRatioPct(value, locale)}
          </span>
        ),
      },
      {
        header: t('adoption.detail.columns.creators'),
        accessor: 'creators',
        sortable: true,
        sortValue: (_v, row: UsageSupplierDayRow) => creatorLabel(row),
        cell: (_v, row: UsageSupplierDayRow) => creatorLabel(row),
      },
    ],
    [t, locale, formatRounded],
  );

  const lineColumns = useMemo<Column[]>(
    () => [
      ...(showGlobalLines
        ? [
            {
              header: t('adoption.detail.columns.date'),
              accessor: 'order_date',
              sortable: true,
              width: '110px',
            } as Column,
            {
              header: t('adoption.detail.columns.supplier'),
              accessor: 'supplier_name',
              sortable: true,
              sortValue: (_v: unknown, row: UsageLineRow) =>
                `${row.supplier_code ?? ''} ${row.supplier_name ?? ''}`,
              cell: (_v: unknown, row: UsageLineRow) =>
                row.supplier_code
                  ? `${row.supplier_code} — ${row.supplier_name ?? ''}`
                  : row.supplier_name ?? '—',
            } as Column,
          ]
        : []),
      {
        header: t('adoption.detail.columns.product'),
        accessor: 'product_name',
        sortable: true,
        sortValue: (_v, row: UsageLineRow) =>
          `${row.product_code ?? ''} ${row.product_name ?? ''}`,
        cell: (_v, row: UsageLineRow) =>
          row.product_code
            ? `${row.product_code} — ${row.product_name ?? ''}`
            : row.product_name ?? '—',
      },
      {
        header: t('adoption.detail.columns.presence'),
        accessor: 'presence_status',
        sortable: true,
        cell: (value: string) => t(`adoption.presence.${value}`, { defaultValue: value }),
        width: '120px',
      },
      {
        header: t('adoption.detail.columns.qtySuggested'),
        accessor: 'qty_suggested',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '100px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
      },
      {
        header: t('adoption.detail.columns.qtyRequested'),
        accessor: 'qty_requested',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '100px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
      },
      {
        header: t('adoption.detail.columns.qtyErp'),
        accessor: 'qty_erp',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '100px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
      },
      {
        header: t('adoption.detail.columns.deltaUser'),
        accessor: 'delta_user',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '90px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (
          <span className={signedDeltaClassName(value)}>
            {formatSignedNumber(value, locale)}
          </span>
        ),
      },
      {
        header: t('adoption.detail.columns.deltaErp'),
        accessor: 'delta_erp',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '90px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (
          <span className={signedDeltaClassName(value)}>
            {formatSignedNumber(value, locale)}
          </span>
        ),
      },
      {
        header: t('adoption.detail.columns.deltaTotal'),
        accessor: 'delta_total',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '90px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (
          <span className={signedDeltaClassName(value)}>
            {formatSignedNumber(value, locale)}
          </span>
        ),
      },
      {
        header: t('adoption.detail.columns.unitCost'),
        accessor: 'unit_cost',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '100px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (value == null ? '—' : formatRounded(value)),
        exportType: 'currency',
      },
      {
        header: t('adoption.detail.columns.deltaTotalAmount'),
        accessor: 'delta_total_amount',
        sortable: true,
        numeric: true,
        align: 'right',
        width: '110px',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) =>
          value == null ? (
            '—'
          ) : (
            <span className={signedDeltaClassName(value)}>{formatRounded(value)}</span>
          ),
        exportType: 'currency',
      },
    ],
    [t, locale, formatRounded, showGlobalLines],
  );

  const errorMessage =
    (supplierDaysQuery.error as { message?: string } | null)?.message ||
    (linesQuery.error as { message?: string } | null)?.message;

  return (
    <div className="space-y-4">
      {errorMessage ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('adoption.loadError')}: {errorMessage}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <span>{t('adoption.detail.statusFilter')}</span>
          <select
            value={statusFilter}
            onChange={(e) => {
              setDetailStatus(e.target.value);
              setPage(1);
              setSelected(null);
              setLinesPage(1);
            }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
          >
            <option value="">{t('adoption.detail.statusAll')}</option>
            <option value="VIA_STOCKERAI_COMPLETO">
              {t('adoption.status.VIA_STOCKERAI_COMPLETO')}
            </option>
            <option value="VIA_STOCKERAI_INCOMPLETO">
              {t('adoption.status.VIA_STOCKERAI_INCOMPLETO')}
            </option>
            <option value="FUERA_DE_STOCKERAI">{t('adoption.status.FUERA_DE_STOCKERAI')}</option>
            <option value="NO_LLEGO_AL_ERP">{t('adoption.status.NO_LLEGO_AL_ERP')}</option>
            <option value="NO_COMPARABLE">{t('adoption.status.NO_COMPARABLE')}</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <span>{t('adoption.detail.presenceFilter')}</span>
          <select
            value={presenceFilter}
            onChange={(e) => {
              setDetailPresence(e.target.value);
              setLinesPage(1);
            }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
          >
            <option value="">{t('adoption.detail.presenceAll')}</option>
            <option value="EN_AMBOS">{t('adoption.presence.EN_AMBOS')}</option>
            <option value="SOLO_ERP">{t('adoption.presence.SOLO_ERP')}</option>
            <option value="SOLO_STOCKERAI">{t('adoption.presence.SOLO_STOCKERAI')}</option>
          </select>
        </label>
        {filters.dateFrom && filters.dateTo ? (
          <p className="text-xs text-gray-500">
            {t('adoption.detail.windowHint', {
              from: filters.dateFrom,
              to: filters.dateTo,
            })}
          </p>
        ) : null}
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-gray-900">
          {t('adoption.detail.supplierDaysTitle')}
        </h2>
        <DataTable
          columns={supplierDayColumns}
          data={supplierDaysQuery.data?.results ?? []}
          loading={supplierDaysQuery.isLoading || supplierDaysQuery.isFetching}
          pagination
          externalPagination={{
            currentPage: supplierDaysQuery.data?.page ?? page,
            totalPages: supplierDaysQuery.data?.num_pages ?? 1,
            totalCount: supplierDaysQuery.data?.count ?? 0,
            pageSize: supplierDaysQuery.data?.page_size ?? supplierDaysPageSize,
            onPageChange: (next) => {
              setPage(next);
              setSelected(null);
            },
            onPageSizeChange: (size) => {
              setSupplierDaysPageSize(size);
              setPage(1);
              setSelected(null);
            },
          }}
          onRowClick={(row: UsageSupplierDayRow) => {
            setSelected(row);
            setLinesPage(1);
          }}
          rowClassName={(row: UsageSupplierDayRow) =>
            selected &&
            selected.order_date === row.order_date &&
            selected.supplier_id === row.supplier_id &&
            selected.warehouse_id === row.warehouse_id
              ? 'bg-stocker-primaryLight/40'
              : ''
          }
          allowExport={allowExport}
          exportFilename="adopcion-pedidos"
          onExport={
            allowExport
              ? async () => {
                  const all: UsageSupplierDayRow[] = [];
                  let pageNum = 1;
                  let numPages = 1;
                  do {
                    const chunk = await usageMonitoringService.getSupplierDays({
                      ...queryParams,
                      status: statusFilter || undefined,
                      page: pageNum,
                      page_size: 200,
                    });
                    all.push(...chunk.results);
                    numPages = chunk.num_pages;
                    pageNum += 1;
                  } while (pageNum <= numPages);
                  return all;
                }
              : undefined
          }
          compact
          resizableColumns
        />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-gray-900">
          {selected
            ? t('adoption.detail.linesTitleSelected', {
                date: selected.order_date,
                supplier: selected.supplier_name || selected.supplier_code || selected.supplier_id,
              })
            : showGlobalLines
              ? t('adoption.detail.linesTitleFiltered')
              : t('adoption.detail.linesTitle')}
        </h2>
        {(selected || showGlobalLines) && (
          <p className="mb-3 text-xs text-gray-500">{t('adoption.detail.deltaLegend')}</p>
        )}
        {(selected || showGlobalLines) && (
          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                {t('adoption.detail.summary.deltaUnits')}
              </p>
              <p
                className={`mt-0.5 text-lg font-semibold tabular-nums ${signedDeltaClassName(biasSummary.deltaUnits)}`}
              >
                {allLinesQuery.isLoading || allLinesQuery.isFetching
                  ? '…'
                  : formatSignedNumber(biasSummary.deltaUnits, locale)}
              </p>
            </div>
            <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                {t('adoption.detail.summary.deltaAmount')}
              </p>
              <p
                className={`mt-0.5 text-lg font-semibold tabular-nums ${signedDeltaClassName(biasSummary.deltaAmount)}`}
              >
                {allLinesQuery.isLoading || allLinesQuery.isFetching
                  ? '…'
                  : biasSummary.deltaAmount == null
                    ? '—'
                    : formatRounded(biasSummary.deltaAmount)}
              </p>
              {biasSummary.linesTotal > 0 ? (
                <p className="mt-0.5 text-[11px] text-gray-500">
                  {t('adoption.detail.summary.costCoverage', {
                    withCost: formatCount(biasSummary.linesWithAmount, locale),
                    total: formatCount(biasSummary.linesTotal, locale),
                  })}
                </p>
              ) : null}
            </div>
            <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
                {t('adoption.detail.summary.biasPct')}
              </p>
              <p
                className={`mt-0.5 text-lg font-semibold tabular-nums ${signedDeltaClassName(biasSummary.biasRatio)}`}
              >
                {allLinesQuery.isLoading || allLinesQuery.isFetching
                  ? '…'
                  : formatSignedRatioPct(biasSummary.biasRatio, locale)}
              </p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                {selected
                  ? t('adoption.detail.summary.scopeOrder')
                  : t('adoption.detail.summary.scopeFilter')}
              </p>
            </div>
          </div>
        )}
        {!selected && !showGlobalLines ? (
          <p className="rounded-md border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            {t('adoption.detail.selectRowHint')}
          </p>
        ) : (
          <DataTable
            columns={lineColumns}
            data={linesQuery.data?.results ?? []}
            loading={linesQuery.isLoading || linesQuery.isFetching}
            pagination
            externalPagination={{
              currentPage: linesQuery.data?.page ?? linesPage,
              totalPages: linesQuery.data?.num_pages ?? 1,
              totalCount: linesQuery.data?.count ?? 0,
              pageSize: linesQuery.data?.page_size ?? linesPageSize,
              onPageChange: setLinesPage,
              onPageSizeChange: (size) => {
                setLinesPageSize(size);
                setLinesPage(1);
              },
            }}
            allowExport={allowExport}
            exportFilename="adopcion-lineas"
            onExport={
              allowExport && linesParams
                ? async () => {
                    const all: UsageLineRow[] = [];
                    let pageNum = 1;
                    let numPages = 1;
                    do {
                      const chunk = await usageMonitoringService.getLines({
                        ...linesParams,
                        page: pageNum,
                        page_size: 200,
                      });
                      all.push(...chunk.results);
                      numPages = chunk.num_pages;
                      pageNum += 1;
                    } while (pageNum <= numPages);
                    return all;
                  }
                : undefined
            }
            compact
            resizableColumns
          />
        )}
      </section>
    </div>
  );
}
